import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {formValueSelector, reduxForm} from 'redux-form';
import {Prompt} from 'react-router-dom';
import Overlay from 'infrastructure/js/components/Overlay/overlay';
import Header, {headerItemsLabels} from '../../Common/Header/header';
import useLabels from 'infrastructure/js/hooks/useLabels';
import NameValuePairs from '../../Common/NameValuePairs/nameValuePairs';
import List from 'infrastructure/js/components/List/list';
import CoCSectionListItem from './CoCSectionListItem/cocSectionListItem';
import CoCResultIndicator from '../CoCResultIndicator/cocResultIndicator';
import {getCategoryByFileType} from '../utils/cocHelper';

import './cocTestForm.scss';

const extractId = (str) => {
  let id = Number(str.split('-')?.[1]);
  return Number.isNaN(id) ? null : id;
}

const getCocTestDetailsInitialData = (initialData, labels) => {
  return (
  [
    {label: labels.get('description'), value: initialData?.cocTestData?.description ?? ''},
    { label: labels.get('material'), value: initialData?.cocTestData?.material ?? ''},
    { label: labels.get('testType'), value: initialData?.cocTestData?.type},
    { label: labels.get('lastUpdate'), value: initialData?.cocTestData?.lastUpdated},
  ]);
}

const getRawMaterialsInitialData = (fields = []) => {
  let res = {};

  fields?.forEach(row => {
    row.forEach(field => {
      res[`id-${field.id}`] = field.value ?? '';
    })
  });

  return res;
}

const getCocSectionsInitialData = (initialData) => {
  return initialData?.cocSectionsData?.map((item, index) => {
    let data = {
      comment: item.hasComment ? item.comment : null,
      rawMaterials: getRawMaterialsInitialData(item.rawMaterialsFields)
    }
    item.attachments?.forEach(item => {
      //NOTE: workaround (for: the entry is not removed by setting its value to undefined ) - set 'N/A' instead of null
      data[`fid-${item.id}`] = item.testResult ?? 'N/A';
    })
    return data;
  });
}

const getFields = (items) => {
  const keys = Object.keys(items);
  return (
    keys.map(key => {
      return {
        fieldId: extractId(key),
        value: items[key],
      }
    })
  );
}

const getAttachments = (items=[]) => {
  return items.map(item => {
    const {id, name, type, temporary, testResult, tempOwner} = item;
    return {
      id,
      originalFileName: name,
      temporary,
      category: getCategoryByFileType(type),
      testResult,
      tempOwner
    }
  });
}

function CocTestForm ({ history,
                    match,
                    handleSubmit,
                    pristine,
                    initialize,
                    change,
                    initialData,
                    actions,
                    loading,
                    cocSectionsListValues,
                  }) {



  const labels = useLabels('mat.forms.cocTestForm.');
  const cocId = match.params.cocId;

  const [testDetails, setTestDetails] = useState([]);
  const [confirmedAbandon, setConfirmedAbandon] = useState(false);
  const nextLocationPath = useRef(null);
  const tempOwner = useRef(null);
  const [pendingNavigationPath, setPendingNavigationPath] = useState(null);

  const [attachmentsCounters, setAttachmentsCounters] = useState([]);
  const [attachmentsFiles, setAttachmentsFiles] = useState([]);
  const [dummyKey, setDummyKey] = useState(0);


  useEffect(() => {
    tempOwner.current = new Date().getTime();

    const counters = [...initialData?.cocAttachmentsCounters || []];
    setAttachmentsCounters(counters);

    const attachedFiles = [...initialData?.cocAttachmentsFiles || []];
    setAttachmentsFiles(attachedFiles);

  }, []);

  useEffect(() => {

    const cocTestDetails = getCocTestDetailsInitialData(initialData, labels)
    setTestDetails(cocTestDetails);

    const initialValues = getCocSectionsInitialData(initialData)
    initialize({ cocSectionsList: initialValues});

  }, [initialData]);

  useEffect(() => {
    if (confirmedAbandon) {
      if ( pendingNavigationPath) {
        history.push(pendingNavigationPath);
        setPendingNavigationPath(null);
        return;
      }
      if (nextLocationPath.current)  {
        history.push(nextLocationPath.current);
      }
      else if (nextLocationPath.current === '') {
        history.goBack();
      }
      nextLocationPath.current = null;
    }
  }, [confirmedAbandon, pendingNavigationPath]);

  const getHeaderItems = () => {
    const {previousId, nextId} = initialData?.cocTestData ?? {};

    return {
      title: initialData?.cocTestData?.name ?? '',
      buttons: [
        {
          id: 'prevTest',
          tooltip: headerItemsLabels.PREVIOUS,
          icon: 'pl pl-arrow-left',
          className: 'prev-test-btn',
          disabled: !previousId,
          action: () => history.push(`/CoC/${cocId}/cocTestForm/${previousId}`),
        },
        {
          id: 'nextTest',
          tooltip: headerItemsLabels.NEXT,
          icon: 'pl pl-arrow-right',
          className: 'next-test-btn',
          disabled: !nextId,
          action: () => history.push(`/CoC/${cocId}/cocTestForm/${nextId}`),
        },

        {
          id: 'cancel',
          label: headerItemsLabels.CANCEL,
          className: 'no-icon',
          action: () => history.push(`/CoC/${cocId}`),
        },
        {
          id: 'save',
          label: headerItemsLabels.SAVE,
          className: 'no-icon',
          action: handleSubmit(onSubmit),
          disabled: pristine ?? loading
        },
      ],
    };
  };

  const onSubmit = (data) => {
    const cocData = initialData?.cocSectionsData; //?.[data.index]

    const sets = data.cocSectionsList?.map((item, index) => {
      return {
        setId: cocData?.[index]?.setId,
        comment: item.comment,
        fields: getFields(item.rawMaterials),
        attachments: getAttachments(attachmentsFiles?.[index])
      }
    });

    const submitData = {
      tempOwner: tempOwner.current,
      sets
    };

    const testId = initialData?.cocTestData?.id;

    actions.cocTestFormActions.submit(submitData, testId, onSubmitCallback);
  }

  const onSubmitCallback = () => {
    setPendingNavigationPath(`/CoC/${cocId}`);
    setConfirmedAbandon(true);
  }

  const handleAbandon = (nextLocation) => {
    if (!confirmedAbandon) {
      actions.cocTestFormActions.abandonDirtyForm(() => {
        setConfirmedAbandon(true);
        nextLocationPath.current = nextLocation.pathname;
      });
      return false;
    }
    return true;
  };

  const onAttachmentUploadValidation = (config) => {
    actions.messageDialogActions.showMessage(config);
  }

  const onAttachmentUpload = (file, type, setId, sectionIndex) => {
    let data = {
      COC_SET: [setId],
      category: getCategoryByFileType(type),
      tempOwner: tempOwner.current,
    }
    actions.cocTestFormActions.uploadCocAttachment(file, data, sectionIndex, uploadCocAttachmentCallback)
  }

  const onAttachmentDownload = (file) => {
    actions.cocTestFormActions.downloadCocAttachment(file);
  }

  const uploadCocAttachmentCallback = (file, sectionIndex) => {
    setAttachmentsFiles(prevFiles => {
      return prevFiles.map((section, index) => {
        if (index === sectionIndex) {
          change(`cocSectionsList[${sectionIndex}].fid-${file.id}`, null)
          return [...section, file];
        }
        return section;
      });
    });

    setAttachmentsCounters(prevCounters => {
      return prevCounters.map((section, index) => {
        if (index === sectionIndex) {
          return section.map(counter => {
            return (counter.type !== file.type) ? counter : { ...counter, attachmentsCount: Math.min(counter.attachmentsCount + 1, counter.attachmentsTotal) };
          });
        }
        return section;
      });
    });
  }

  const onAttachmentRemove = (file, sectionIndex) => {
    if (!file.temporary) { //remove the not temporary attachments only on Client side. They will be updated on submit
      return removeCocAttachmentCallback(file, sectionIndex);
    }
    let data = {
      ids: [file.id]
    }
    actions.cocTestFormActions.removeCocAttachment(file, data, sectionIndex, removeCocAttachmentCallback)
  }

  const removeCocAttachmentCallback = (file, sectionIndex) => {
    setAttachmentsFiles(prevFiles => {
      return prevFiles.map((section, index) => {
        if (index === sectionIndex) {
          //NOTE: workaround for: the redux-form entry does not get removed by setting its value to undefined.
          // 1. for the attachments that are loaded from the file system (not saved yet
          // and have file.file property) - set the redux-form value to undefined in order to remove its entry from the redux-form store;
          // 2. for the attachments that were fetched from the server side - set the redux-form value to null in order to change 'pristine';
          change(`cocSectionsList[${sectionIndex}].fid-${file.id}`, file.file ? undefined : null);
          return section.filter(item => item.id !== file.id);
        }
        return section;
      });
    });

    setAttachmentsCounters(prevCounters => {
      return prevCounters.map((section, index) => {
        if (index === sectionIndex) {
          return section.map(counter => {
            return (counter.type !== file.type) ? counter : { ...counter, attachmentsCount: Math.max(counter.attachmentsCount - 1, 0) };
          });
        }
        return section;
      });
    });
  };

  const onAttachmentClick = (file, sectionIndex) => {
    if (file.temporary || file.isExpanded) {
      setAttachmentsFiles(prevFiles => {
        return prevFiles.map((section, index) => {
          if (index === sectionIndex) {
            return section.map(item => {
              if (item.id === file.id && (item.isExpanded || item.file)) {
                return {...item, isExpanded: !item.isExpanded};
              }
              return item;
            });
          }
          return section;
        });
      });
      setDummyKey(prev => 1 - prev);
    }
    else {
      actions.cocTestFormActions.getCocAttachmentUrl(file, sectionIndex, onAttachmentClickCallback);
    }
  }

  const onAttachmentClickCallback = (data, file, sectionIndex) => {
    setAttachmentsFiles(prevFiles => {
      return prevFiles.map((section, index) => {
        if (index === sectionIndex) {
          return section.map(item => {
            if (item.id === file.id) {
              return {...item, url: data.url, isExpanded: true};
            }
            return item;
          });
        }
        return section;
      });
    });
  }

  const onAttachmentResultChange = (value, file, sectionIndex) => {
    setAttachmentsFiles(prevFiles => {
      return prevFiles.map((section, index) => {
        if (index === sectionIndex) {
          return section.map(item => {
            if (item.id === file.id) {
              item.testResult = value;
            }
            return item;
          });
        }
        return section;
      });
    });
  }

  const cocSectionItemRenderer = (data) => {

    return (
      <CoCSectionListItem
        {...data}
        change={change}
        cocData={initialData?.cocSectionsData?.[data.index] ?? {}}
        reduxData={cocSectionsListValues?.[data.index] ?? {}}
        isViewOnly={initialData?.cocTestData?.isViewOnly}
        attachmentsCounters={attachmentsCounters?.[data.index] ?? []}
        attachmentsFiles={attachmentsFiles?.[data.index] ?? []}
        onAttachmentUpload={onAttachmentUpload}
        onAttachmentUploadValidation={onAttachmentUploadValidation}
        onAttachmentDownload={onAttachmentDownload}
        onAttachmentRemove={onAttachmentRemove}
        onAttachmentClick={onAttachmentClick}
        onAttachmentResultChange={onAttachmentResultChange}
      />
    )
  }

  const renderCoCSectionsList = () => {
    return (
      <div className="coc-sections-list-container">
        <List
          updateKey={dummyKey?.toString()}
          id="cocSectionsList"
          name="cocSectionsList"
          className='coc-sections-list'
          itemRenderer={cocSectionItemRenderer}
          preSelectedItems={[null]}
          isViewOnly={true}
        />
      </div>
    )
  }

  const getHeaderLeftContent = () => {
    return (
      <CoCResultIndicator result={initialData?.cocTestData?.testResult?.toLowerCase()} />
    );
  }

  const renderLoadingOverlay = () => {
    if (loading) {
      return <Overlay.Loading />;
    }
    return null;
  };

  return (
    <div className="pl-form coc-test-form">
      <div className='header-wrapper'>
        <Header className='form-header' {...getHeaderItems()} leftContent={getHeaderLeftContent()}/>
      </div>

      <div className='coc-test-details-section'>
        <NameValuePairs items={testDetails}/>
      </div>

      {renderCoCSectionsList()}

      <Prompt when={!pristine} message={handleAbandon}/>
      {renderLoadingOverlay()}
    </div>
  );
}


CocTestForm.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  initialize: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  actions: PropTypes.object.isRequired,
};

const selector = formValueSelector('cocTestForm');
const mapStateToProps = (state) => {
  return {
    cocSectionsListValues: selector(state, 'cocSectionsList'),
  };
};

const reduxFormConfig = {
  form: 'cocTestForm',
}

export default compose(connect(mapStateToProps), reduxForm(reduxFormConfig))(CocTestForm);

